"use client";

import React, { useEffect, useState } from "react";
import { X, ArrowRight } from "lucide-react";
import Select from "react-select";
import { stateCityData } from "@/utils/stateCityData";
import { submitLeadMeritto } from "@/helpers/submitLeadMeritto";

const UserFormModal = ({ isOpen, onClose, onSuccess, shouldDownload, courseDetails, schoolName, course, specialization,stream }) => {
  const [loading, setLoading] = useState(false);
  const INITIAL_FORM_STATE = {
    name: "",
    email: "",
    phone: "",
    programme: "",
    Specialization: "",
    state: "",
    city: "",
    downloadBrochureStatus: false,
  };

  const [form, setForm] = useState(INITIAL_FORM_STATE);
  const [cities, setCities] = useState([]);
  const [programmeOptions, setProgrammeOptions] = useState([]);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  const emailRegex = /^[a-zA-Z0-9._%+-]{4,}@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,7}$/;
  const phoneRegex = /^(?:\+91|91)?[6-9]\d{9}$/;

  const formattedStates = Object.keys(stateCityData).map((s) => ({
    value: s,
    label: s,
  }));

  const fetchCities = (stateName) => {
    const cityList = stateCityData[stateName] || [];
    setCities(cityList.map((c) => ({ value: c, label: c })));
  };

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
      setFormError("");
      setFormSuccess("");
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  // Sync Download Status
  useEffect(() => {
    if (isOpen) {
      setForm(prev => ({
        ...prev,
        downloadBrochureStatus: Boolean(shouldDownload),
      }));
    }
  }, [shouldDownload, isOpen]);

  /* ----------- HANDLE CHANGE ----------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFormError("");
    setFormSuccess("");
    if (name === "state") {
      fetchCities(value);
      setForm((prev) => ({ ...prev, city: "" }));
    }
  };

  /* ----------- LOAD PROGRAMME ----------- */
  useEffect(() => {
    if (!courseDetails) return;

    const programName = courseDetails.programDetails.program;
    const specializations = courseDetails.programDetails.specialization || [];

    const formatted = [
      {
        label: programName,
        options: specializations.map((spec) => ({
          value: `${programName}|||${spec}`,
          label: spec,
        })),
      },
    ];

    setProgrammeOptions(formatted);

    setForm((prev) => ({
      ...prev,
      programme: programName,
    }));
  }, [courseDetails]);

  const handleSelectChange = (name, selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setFormError("");
    setFormSuccess("");
    if (name === "Specialization") {
      const [program, specialization] = value.split("|||");
      setForm((prev) => ({
        ...prev,
        programme: program,
        Specialization: specialization,
      }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));

    if (name === "state") {
      fetchCities(value);
      setForm((prev) => ({ ...prev, city: "" }));
    }
  };


  // Reset Form
  const resetForm = () => {
    setForm({
      name: "",
      email: "",
      phone: "",
      programme: courseDetails?.programDetails?.program || "",
      Specialization: specialization || "",
      state: "",
      city: "",
      downloadBrochureStatus: false,
    });
  };

  /* ----------- SUBMIT ----------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setFormError("");
    setFormSuccess("");
    const { name, email, phone, programme, Specialization, state, city } = form;

    if (!name.trim()) {
      setFormError("Please enter your name.");
      return;
    }
    if (!email.trim()) {
      setFormError("Please enter your email address.");
      return;
    }
    if (!emailRegex.test(email)) {
      setFormError("Please enter a valid email address");
      return;
    }
    if (!phone.trim()) {
      setFormError("Please enter your mobile number.");
      return;
    }
    if (!phoneRegex.test(phone)) {
      setFormError("Please enter a valid 10-digit mobile number");
      return;
    }

    if (!programme) {
      setFormError("Please select a programme.");
      return;
    }

    if (!Specialization) {
      setFormError("Please select a specialization.");
      return;
    }

    if (!state) {
      setFormError("Please select your state.");
      return;
    }

    if (!city) {
      setFormError("Please select your city.");
      return;
    }

    setLoading(true);

    try {

      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        mobile: form.phone,
        state: form.state,
        city:form.city,
        campus: schoolName,
        course: course,
        specialization: specialization,
        country_dial_code: "+91",

        source: 'direct',
        medium: 'direct',
        campaign: 'Co-Led',
      };


      console.log("payload", payload);

      await submitLeadMeritto(payload);

      const res = await fetch(
        "https://lp.msu.edu.in/lead/api/industry-co-lead/redversity",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(form),
        }
      );

      const data = await res.json();

      if (!data.success) {
        throw new Error(data.message);
      }

      setFormSuccess("Form submitted successfully!");

      // setForm(INITIAL_FORM_STATE);
      resetForm();
      setCities([]);
      setProgrammeOptions(programmeOptions);
      setFormError("");
      setTimeout(() => {
        onClose();
        onSuccess?.();
      }, 3000)

    } catch (error) {
      setFormError("Submission failed");
    } finally {
      setLoading(false);
    }
  };

  /* ----------- REACT SELECT STYLES ----------- */
  const customStyles = {
    control: (base) => ({
      ...base,
      padding: "2px",
      borderColor: "#e5e7eb",
      borderRadius: "0.25rem",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#d1d5db",
      },
    }),
    placeholder: (base) => ({
      ...base,
      color: "#9ca3af",
    }),
    groupHeading: (base) => ({
      ...base,
      color: "#1f2937",
      fontWeight: "bold",
    }),
  };

  const formattedCities = cities;


  /* ----------- UI ----------- */
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-9999 flex items-center justify-center px-3">
      <div onClick={onClose} className="absolute inset-0 bg-black/60" />

      <div className="relative w-full max-w-md bg-white rounded-lg p-6 z-50">
        <X
          className="cursor-pointer absolute right-4 top-4"
          onClick={onClose}
        />

        <div className="text-xl font-semibold mb-5">Let's Get You Started!</div>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Name"
            className="w-full border p-2 rounded"
          />
          <input
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="Email"
            className="w-full border p-2 rounded"
          />
          <input
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder="Phone"
            className="w-full border p-2 rounded"
          />

          <Select
            name="Specialization"
            className="w-full border rounded"
            placeholder="Select Specialization"
            options={programmeOptions}
            onChange={(opt) => handleSelectChange("Specialization", opt)}
            styles={customStyles}
            maxMenuHeight={200}
            value={
              form.Specialization
                ? {
                  value: `${form.programme}|||${form.Specialization}`,
                  label: form.Specialization,
                }
                : null
            }
          />

          <Select
            name="state"
            placeholder="Select State"
            className="w-full border rounded"
            options={formattedStates}
            onChange={(opt) => handleSelectChange("state", opt)}
            styles={customStyles}
            maxMenuHeight={200}
            value={
              form.state
                ? formattedStates.find((s) => s.value === form.state)
                : null
            }
          />

          <Select
            name="city"
            placeholder="Select City"
            className="w-full border rounded"
            options={formattedCities}
            onChange={(opt) => handleSelectChange("city", opt)}
            styles={customStyles}
            maxMenuHeight={200}
            value={
              form.city
                ? formattedCities.find((c) => c.value === form.city)
                : null
            }
          />

          {formError && (
            <p className="text-red-500 text-sm text-center">{formError}</p>
          )}

          {formSuccess && (
            <p className="text-[#0B202C] text-sm text-center">{formSuccess}</p>
          )}

          <button
            disabled={loading}
            className="w-full bg-[#F7A400] py-2 rounded font-semibold flex justify-center gap-2 cursor-pointer"
          >
            {loading ? "Submitting..." : "SUBMIT"} <ArrowRight />
          </button>
        </form>
      </div>
    </div>
  );
};

export default UserFormModal;